import { createTheme, PaletteColor } from '@mui/material/styles';
import { TypographyOptions } from '@mui/material/styles/createTypography';
import { queryBuilderCssBaselineOverrides, queryBuilderTooltipOverrides } from './queryBuilderThemeStyles';

/**
 * New Brand Theme - Light Blue variant
 * Replaces the Transcepta blue (#0E6EB2) with a lighter blue palette (#0BA2E5)
 */

/** Primary UI font for new brand (Inter — load via portal `public/index.html` Google Fonts link). */
const newBrandFontStack = "'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif";

const typography: TypographyOptions = {
    fontFamily: newBrandFontStack,
    h1: {
        fontSize: 28,
        lineHeight: '36px',
    },
    h2: {
        fontSize: 24,
        lineHeight: '32px',
    },
    h3: {
        fontSize: 22,
        lineHeight: '28px',
        fontWeight: 700,
    },
    h4: {
        fontSize: 22,
        lineHeight: '28px',
        fontWeight: 700,
    },
    h5: {
        fontSize: 22,
        lineHeight: '28px',
        fontWeight: 700,
    },
    h6: {
        fontSize: 22,
        lineHeight: '28px',
        fontWeight: 700,
    },
    subtitle1: {
        fontSize: 16,
        lineHeight: '24px',
        letterSpacing: '0.15px',
        fontWeight: 700,
    },
    subtitle2: {
        fontSize: 14,
        lineHeight: '20px',
        letterSpacing: '0.1px',
        fontWeight: 600,
    },
    body1: {
        fontSize: 14,
        lineHeight: '20px',
        letterSpacing: '0.25px',
    },
    body2: {
        fontSize: 12,
        lineHeight: '16px',
        letterSpacing: '0.4px',
    },
    caption: {
        fontSize: 14,
        lineHeight: '20px',
        letterSpacing: '0.1px',
        fontWeight: 700,
    },
    button: {
        fontSize: 14,
        lineHeight: '20px',
        letterSpacing: '0.1px',
        fontWeight: 600,
        textTransform: 'none',
    },
};

const breakpoints = {
    values: {
        xs: 0,
        mobile: 570,
        sm: 768,
        md: 1024,
        lg: 1200,
        xl: 1536,
    },
};

// New brand color palette - Light blue (#0BA2E5)
const newBrandPalette = {
    '900': '#044B6A',
    '800': '#066A95',
    '700': '#0889BF',
    '600': '#09A0DB',
    '500': '#0BA2E5', // Main light blue
    '400': '#2EB3EA',
    '300': '#5BC4EF',
    '200': '#89D5F4',
    '100': '#B7E6F9',
    '50': '#E5F6FD',
    main: '#0BA2E5',
    dark: '#066A95',
    light: '#5BC4EF',
    contrastText: '#FFFFFF', // Force white text on primary color backgrounds
};

const primaryText = '#25292F';

const newBrandSecondary: PaletteColor = {
    main: '#FC7500',
    dark: '#E06A00',
    light: '#FF9A40',
    contrastText: '#FFFFFF',
};

const gridPalette = {
    header: '#F5F5F5',
    border: '#F7F7F7',
    activePage: '#E5E5E5',
    bg: '#FFFFFF',
};

const statusPalette = {
    info: {
        background: 'rgba(11, 162, 229, 0.15)',
        color: newBrandPalette.main,
    },
    rejected: {
        background: 'rgba(214, 77, 77, 0.15)',
        color: '#C33030',
    },
    parked: {
        background: 'rgba(223, 132, 207, 0.2)',
        color: '#B24CA6',
    },
    paid: {
        background: 'rgba(81, 189, 113, 0.2)',
        color: '#2B8E50',
    },
    submitted: {
        background: 'rgba(222, 199, 255, 0.35)',
        color: '#A551B5',
    },
};

/** Multi-series dashboard chart colors (cycles when more series/slices than entries). */
export const newBrandDashboardChartOrdinal = [
    '#FC7500',
    '#14AFF1',
    '#8FD163',
    '#FFB800',
    '#FF4F79',
    '#36D1DA',
    '#C17EE4',
    '#7A72E4',
    '#EE524B',
    '#EC79A3',
    '#E99C56',
] as const;

export const newBrandLightTheme = createTheme({
    palette: {
        mode: 'light',
        black: primaryText,
        white: '#FCFEFF',
        primary: newBrandPalette,
        secondary: newBrandPalette,
        error: {
            main: '#B63425',
            '900': '#49150F',
            '800': '#6D1F16',
            '700': '#922A1E',
            '600': '#A42F21',
            '500': '#B63425',
            '400': '#C55D51',
            '300': '#D3857C',
            '200': '#E2AEA8',
            '100': '#F0D6D3',
            '50': '#F8EBE9',
        },
        info: newBrandPalette,
        success: {
            main: '#52BD64',
            '900': '#214C28',
            '800': '#31713C',
            '700': '#429750',
            '600': '#4AAA5A',
            '500': '#52BD64',
            '400': '#75CA83',
            '300': '#97D7A2',
            '200': '#BAE5C1',
            '100': '#DCF2E0',
            '50': '#EEF8EF',
            contrastText: '#FFFFFF',
        },
        warning: {
            main: '#FFCC48',
            '900': '#66521D',
            '800': '#997A2B',
            '700': '#CCA33A',
            '600': '#CCA33A',
            '500': '#FFCC48',
            '400': '#FFD66D',
            '300': '#FFE091',
            '200': '#FFEBB6',
            '100': '#FFF5DA',
            '50': '#FFFAED',
        },
        background: {
            paper: '#FFFFFF', // Left panel BG
            default: '#F7F7F7', // Main BG
        },
        text: {
            primary: primaryText,
        },
        grey: {
            '900': '#41444C',
            '800': '#626671',
            '700': '#828897',
            '600': '#9399AA',
            '500': '#A3AABD',
            '400': '#B5BBCA',
            '300': '#C8CCD7',
            '200': '#DADDE5',
            '100': '#EDEEF2',
            '50': '#F6F6F8',
        },
        brandSecondary: newBrandSecondary,
        dashboardChartOrdinal: [...newBrandDashboardChartOrdinal],
    },
    typography,
    breakpoints,
});

newBrandLightTheme.components = {
    MuiButtonBase: {
        defaultProps: {
            disableRipple: true,
        },
    },
    // Ensure all Typography uses theme font (including inside Highcharts portals)
    MuiTypography: {
        styleOverrides: {
            root: {
                fontFamily: `${newBrandFontStack} !important`,
            },
        },
    },
    // Link styling - uses new brand light blue colors
    MuiLink: {
        styleOverrides: {
            root: {
                color: newBrandLightTheme.palette.primary.main,
                textDecoration: 'none',
                '&:hover': {
                    color: newBrandLightTheme.palette.primary.light,
                    textDecoration: 'underline',
                },
                '&:active': {
                    color: newBrandLightTheme.palette.primary.dark,
                },
            },
        },
    },
    MuiCssBaseline: {
        styleOverrides: {
            // Set CSS custom property for chart fonts (Highcharts)
            ':root': {
                '--chart-font-family': newBrandFontStack,
            },
            // Global anchor tag styling
            a: {
                color: newBrandLightTheme.palette.primary.main,
                textDecoration: 'none',
                transition: 'color 0.2s ease',
                '&:hover': {
                    color: newBrandLightTheme.palette.primary.light,
                    textDecoration: 'underline',
                },
                '&:active': {
                    color: newBrandLightTheme.palette.primary.dark,
                },
            },
            // Header nav tab selected styling - white underline for new brand
            '.header-nav-tab-selected': {
                borderBottom: '6px solid',
                borderColor: '#FFFFFF',
            },
            // Left nav item styling - light blue selection with accent bar
            '.left-nav-item': {
                '&:hover': {
                    backgroundColor: '#D8F1FD',
                },
            },
            '.left-nav-item-selected': {
                backgroundColor: '#D8F1FD !important',
                color: `${primaryText} !important`,
                borderRight: `6px solid ${newBrandPalette.main} !important`,
                '&:hover': {
                    backgroundColor: '#D8F1FD !important',
                    color: `${primaryText} !important`,
                },
            },
            '.portal-nav-link': {
                outline: 'none',
                '&:focus-visible': {
                    outline: `2px solid ${newBrandSecondary.main}`,
                    outlineOffset: -2,
                },
            },
            // Legacy SCSS gives .mainContainer 2rem of padding all around; on the new
            // brand the sub-nav already provides visual separation, so drop the top.
            '.mainContainer': {
                paddingTop: '0 !important',
            },
            // Status chip color classes — applied via `className` on <Chip /> in search grids.
            '.status-chip-green': { color: '#305318 !important', backgroundColor: '#E5F4DC !important' },
            '.status-chip-pink': { color: '#AA3377 !important', backgroundColor: '#F7EBF1 !important' },
            '.status-chip-red': { color: '#912018 !important', backgroundColor: '#FEE4E2 !important' },
            '.status-chip-blue': { color: '#054561 !important', backgroundColor: '#D8F1FD !important' },
            // Lift UserPilot above the footer on desktop. It positions itself with `top: 98%` +
            // a transform, so we have to clear those before bottom kicks in.
            '@media (min-width: 900px)': {
                '#userpilot-resource-centre-frame': {
                    top: 'auto !important',
                    bottom: '100px !important',
                    transform: 'none !important',
                },
            },
            ...queryBuilderCssBaselineOverrides,
        },
    },
    MuiBreadcrumbs: {
        styleOverrides: {
            root: newBrandLightTheme.palette.text.primary,
            li: {
                a: {
                    textDecoration: 'none',
                    color: newBrandLightTheme.palette.text.primary,
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                },
                p: {
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                },
            },
        },
    },
    MuiBackdrop: {
        styleOverrides: {
            root: {
                backgroundColor: 'rgba(4, 75, 106, 0.50)', // Light blue-tinted backdrop
            },
        },
    },
    MuiDialog: {
        styleOverrides: {
            paper: {
                maxWidth: 1200,
            },
        },
    },
    MuiAlert: {
        styleOverrides: {
            standardWarning: {
                backgroundColor: '#fef0c7',
                '& .MuiAlert-icon': {
                    color: '#f79009',
                },
            },
        },
    },
    MuiAccordionSummary: {
        styleOverrides: {
            root: {
                '& .MuiAccordionSummary-content': {
                    justifyContent: 'space-between',
                    mt: '10px',
                    mb: '10px',
                },
            },
        },
    },
    MuiInputLabel: {
        styleOverrides: {
            root: {
                '&:not(.MuiInputLabel-shrink)': {
                    top: -8,
                },
            },
        },
    },
    MuiOutlinedInput: {
        styleOverrides: {
            input: {
                padding: '6.5px 12px !important',
            },
        },
    },
    MuiAutocomplete: {
        styleOverrides: {
            input: {
                padding: '0px 4px !important',
                height: '1.125em !important',
                width: '100%',
            },
        },
    },
    MuiRadio: {
        styleOverrides: {
            root: {
                '&:hover': {
                    backgroundColor: 'rgba(11, 162, 229, 0.12)', // Light blue hover
                },
                '&:active': {
                    backgroundColor: 'rgba(11, 162, 229, 0.24)', // Light blue active
                },
                '&:focus': {
                    border: '1px solid',
                    borderColor: '#044B6A',
                },
            },
        },
    },
    // Outlined button styling - #076792
    MuiButton: {
        styleOverrides: {
            outlined: {
                borderColor: '#076792',
                color: '#076792',
                '&:hover': {
                    borderColor: '#076792',
                    backgroundColor: 'rgba(7, 103, 146, 0.08)',
                },
            },
        },
    },
    // Left panel (Drawer) background - white (#FFFFFF)
    MuiDrawer: {
        styleOverrides: {
            paper: {
                backgroundColor: '#FFFFFF',
            },
        },
    },
    MuiDataGrid: {
        styleOverrides: {
            root: {
                border: `1px solid ${gridPalette.border}`,
                backgroundColor: gridPalette.bg,
                '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: gridPalette.header,
                    borderBottom: `1px solid ${gridPalette.border}`,
                },
                '& .MuiDataGrid-row': {
                    backgroundColor: `${gridPalette.bg} !important`, // Force white background
                    '&:hover': {
                        backgroundColor: `${gridPalette.header} !important`, // Hover effect
                    },
                },
                '& .MuiDataGrid-row--alternating': {
                    backgroundColor: `${gridPalette.bg} !important`, // Disable alternating
                },
                '& .MuiDataGrid-cell': {
                    borderColor: gridPalette.border,
                },
                '& .MuiDataGrid-columnSeparator': {
                    display: 'none',
                },
                '& .MuiDataGrid-footerContainer': {
                    borderTop: `1px solid ${gridPalette.border}`,
                },
                '& .td.status': {
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 999,
                    padding: '4px 12px',
                    fontSize: 'inherit',
                    lineHeight: 'inherit',
                    fontWeight: 400,
                    textTransform: 'none',
                    backgroundColor: `${statusPalette.info.background}`,
                    color: `${statusPalette.info.color}`,
                },
                '& .td.status-info': {
                    backgroundColor: statusPalette.info.background,
                    color: statusPalette.info.color,
                },
                '& .td.status-rejected': {
                    backgroundColor: statusPalette.rejected.background,
                    color: statusPalette.rejected.color,
                },
                '& .td.status-parked': {
                    backgroundColor: statusPalette.parked.background,
                    color: statusPalette.parked.color,
                },
                '& .td.status-paid': {
                    backgroundColor: statusPalette.paid.background,
                    color: statusPalette.paid.color,
                },
                '& .td.status-submitted': {
                    backgroundColor: statusPalette.submitted.background,
                    color: statusPalette.submitted.color,
                },
            },
        },
    },
    MuiPaginationItem: {
        styleOverrides: {
            root: {
                color: `${primaryText} !important`,
                '&.Mui-selected': {
                    backgroundColor: `${gridPalette.activePage} !important`,
                    color: `${primaryText} !important`,
                },
                // Explicitly target Previous/Next buttons to ensure they are black (primaryText)
                '&.MuiPaginationItem-previousNext': {
                    color: `${primaryText} !important`,
                },
            },
        },
    },
    MuiTooltip: {
        styleOverrides: queryBuilderTooltipOverrides,
    },
};

export const newBrandDarkTheme = createTheme({
    palette: {
        mode: 'dark',
        black: '#25292F',
        white: '#FCFEFF',
        primary: newBrandPalette,
        secondary: newBrandPalette,
        error: {
            main: '#B63425',
            '900': '#49150F',
            '800': '#6D1F16',
            '700': '#922A1E',
            '600': '#A42F21',
            '500': '#B63425',
            '400': '#C55D51',
            '300': '#D3857C',
            '200': '#E2AEA8',
            '100': '#F0D6D3',
            '50': '#F8EBE9',
        },
        info: newBrandPalette,
        success: {
            main: '#52BD64',
            '900': '#214C28',
            '800': '#31713C',
            '700': '#429750',
            '600': '#4AAA5A',
            '500': '#52BD64',
            '400': '#75CA83',
            '300': '#97D7A2',
            '200': '#BAE5C1',
            '100': '#DCF2E0',
            '50': '#EEF8EF',
        },
        warning: {
            main: '#52BD64',
            '900': '#66521D',
            '800': '#997A2B',
            '700': '#CCA33A',
            '600': '#CCA33A',
            '500': '#FFCC48',
            '400': '#FFD66D',
            '300': '#FFE091',
            '200': '#FFEBB6',
            '100': '#FFF5DA',
            '50': '#FFFAED',
        },
        background: {
            paper: '#1f1f1f',
            default: '#2a2c34',
        },
        grey: {
            '900': '#2e3036',
            '800': '#4a4d56',
            '700': '#666a76',
            '600': '#787b8a',
            '500': '#8a8d9e',
            '400': '#9ba0b1',
            '300': '#acb1c2',
            '200': '#bdc2d3',
            '100': '#cfd3e4',
            '50': '#d8d9e0',
        },
        text: {
            primary: '#FCFEFF',
        },
        brandSecondary: newBrandSecondary,
        dashboardChartOrdinal: [...newBrandDashboardChartOrdinal],
    },
    typography,
    breakpoints,
});

newBrandDarkTheme.components = {
    MuiButtonBase: {
        defaultProps: {
            disableRipple: true,
        },
    },
    // Ensure all Typography uses theme font (including inside Highcharts portals)
    MuiTypography: {
        styleOverrides: {
            root: {
                fontFamily: `${newBrandFontStack} !important`,
            },
        },
    },
    // Link styling - uses new brand light blue colors
    MuiLink: {
        styleOverrides: {
            root: {
                color: newBrandDarkTheme.palette.primary.light,
                textDecoration: 'none',
                '&:hover': {
                    color: newBrandPalette['100'],
                    textDecoration: 'underline',
                },
                '&:active': {
                    color: newBrandDarkTheme.palette.primary.main,
                },
            },
        },
    },
    MuiCssBaseline: {
        styleOverrides: {
            // Set CSS custom property for chart fonts (Highcharts)
            ':root': {
                '--chart-font-family': newBrandFontStack,
            },
            // Global anchor tag styling
            a: {
                color: newBrandDarkTheme.palette.primary.light,
                textDecoration: 'none',
                transition: 'color 0.2s ease',
                '&:hover': {
                    color: newBrandPalette['100'],
                    textDecoration: 'underline',
                },
                '&:active': {
                    color: newBrandDarkTheme.palette.primary.main,
                },
            },
            ...queryBuilderCssBaselineOverrides,
        },
    },
    MuiDialog: {
        styleOverrides: {
            paper: {
                maxWidth: 1200,
                backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.08))',
            },
        },
    },
    MuiAccordionSummary: {
        styleOverrides: {
            root: {
                '& .MuiAccordionSummary-content': {
                    justifyContent: 'space-between',
                },
            },
        },
    },
    MuiBackdrop: {
        styleOverrides: {
            root: {
                backgroundColor: 'rgba(1, 1, 20, 0.50)',
            },
        },
    },
    MuiBreadcrumbs: {
        styleOverrides: {
            root: newBrandDarkTheme.palette.text.primary,
            li: {
                a: {
                    textDecoration: 'none',
                    color: newBrandDarkTheme.palette.text.primary,
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                },
                p: {
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                },
            },
        },
    },
    MuiInputLabel: {
        styleOverrides: {
            root: {
                '&:not(.MuiInputLabel-shrink)': {
                    top: -8,
                },
            },
        },
    },
    MuiOutlinedInput: {
        styleOverrides: {
            input: {
                padding: '6.5px 12px !important',
            },
        },
    },
    MuiAutocomplete: {
        styleOverrides: {
            input: {
                padding: '0px 4px !important',
                height: '1.125em !important',
            },
        },
    },
    MuiRadio: {
        styleOverrides: {
            root: {
                '&:hover': {
                    backgroundColor: 'rgba(11, 162, 229, 0.12)',
                },
                '&:active': {
                    backgroundColor: 'rgba(11, 162, 229, 0.24)',
                },
                '&:focus': {
                    border: '1px solid',
                    borderColor: '#044B6A',
                },
            },
        },
    },
    // Top nav active bar underline - uses primary color
    MuiTabs: {
        styleOverrides: {
            indicator: {
                backgroundColor: newBrandPalette.main,
                height: 3,
            },
        },
    },
    // Left nav background styling (dark mode uses darker shades)
    MuiListItemButton: {
        styleOverrides: {
            root: {
                '&.Mui-selected': {
                    backgroundColor: 'rgba(11, 162, 229, 0.16)',
                    borderLeft: `3px solid ${newBrandPalette.main}`,
                    '&:hover': {
                        backgroundColor: 'rgba(11, 162, 229, 0.24)',
                    },
                },
                '&:hover': {
                    backgroundColor: 'rgba(11, 162, 229, 0.08)',
                },
            },
        },
    },
    // Outlined button styling
    MuiButton: {
        styleOverrides: {
            outlined: {
                borderColor: newBrandPalette.light,
                color: newBrandPalette.light,
                '&:hover': {
                    borderColor: newBrandPalette.main,
                    backgroundColor: 'rgba(11, 162, 229, 0.12)',
                },
            },
        },
    },
    MuiTooltip: {
        styleOverrides: queryBuilderTooltipOverrides,
    },
};
